// src/features/hmi-simulator/components/screens/LockerScreen.tsx
import React from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Stack,
  CircularProgress
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Header } from '../shared/Header';
import { HMIConfig, LockerInfo } from '../../types';

interface LockerScreenProps {
  config: HMIConfig;
  language: string;
  setLanguage: (lang: string) => void;
  isOnline: boolean;
  apiLoading: boolean;
  t: (key: string) => string;
  api: any;
  setScreen: (screen: any) => void;
  currentLocker: LockerInfo | null;
  lockerAction: 'confirm' | 'checkout' | null;
  error: string;
  onConfirm: () => void;
  onCheckout: () => void;
  onCancel: () => void;
}

export const LockerScreen: React.FC<LockerScreenProps> = ({
  config,
  language,
  setLanguage,
  isOnline,
  apiLoading,
  t,
  api,
  setScreen,
  currentLocker,
  lockerAction,
  error,
  onConfirm,
  onCheckout,
  onCancel
}) => {
  const renderLockerContent = () => {
    if (lockerAction === 'confirm') {
      return (
        <>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Locker Opened!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please store your belongings and close the door.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            The locker will be assigned to you once you close the door.
          </Typography>
        </>
      );
    }
    
    if (lockerAction === 'checkout') {
      return (
        <>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Checkout Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please take all your belongings and close the door.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Thank you for using our locker service.
          </Typography>
        </>
      );
    }
    
    return (
      <>
        <Typography variant="h6" color={config.primaryColor} gutterBottom>
          {t('your_locker')}
        </Typography>
        <Typography variant="h1" sx={{ my: 4, color: config.primaryColor, fontWeight: 'bold' }}>
          {currentLocker?.lockerName}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          {currentLocker?.action === 'first_time' 
            ? 'Please store your belongings and close the door'
            : 'Please take your belongings and close the door'}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            onClick={onCancel}
            size="large"
          >
            {t('button_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={currentLocker?.action === 'first_time' ? onConfirm : onCheckout}
            disabled={apiLoading}
            sx={{ backgroundColor: config.primaryColor }}
            size="large"
          >
            {apiLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('button_open')
            )}
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Header
        config={config}
        language={language}
        setLanguage={setLanguage}
        isOnline={isOnline}
        deviceId={api.getCredentials()?.deviceId}
        onSettingsClick={() => setScreen('settings')}
      />
      
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            {renderLockerContent()}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};